import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import EuroBand from '../EuroBand';
import VehicleBrandLogo from '../VehicleBrandLogo';
import { vehiclesAPI } from '../../services/api';
import { ga } from '../../services/ga';
import { vehicleStore } from '../../services/vehicleStore';
import { useT } from '../../services/i18n';
import { getFuelColor, getFuelLabel } from './useVehicleData';

const formatPlate = (raw) => {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/[IOU]/g, '').slice(0, 7);
  if (/^[A-Z]{2}/.test(clean)) return [clean.slice(0, 2), clean.slice(2, 5), clean.slice(5, 7)].filter(Boolean).join('-');
  return clean;
};

export const PlateSearch = ({ result, setResult, onIdentified }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const [plate, setPlate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = async () => {
    if (!plate.trim()) { setError(t('home.plateRequired')); return; }
    setLoading(true); setError(''); setResult(null);
    try {
      const r = await vehiclesAPI.lookupPlate(plate);
      onIdentified(r);
      setResult(r);
      vehicleStore.set({ brand: r.brand, model: r.model, year: r.year, engine: r.engine, plate: r.plate });
      ga.search(`${r.brand} ${r.model}`, 'plate', { search_results: 1, vehicle_brand: r.brand, vehicle_model: r.model, vehicle_engine: r.engine });
    } catch (e) {
      ga.search('(plaque)', 'plate', { search_results: 0 });
      setError(e.response?.data?.detail || t('home.plateNotFound'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="relative flex items-center my-6">
        <div className="flex-1 border-t border-gray-200"></div>
        <span className="px-4 text-sm text-gray-500 bg-white">{t('home.orPlate')}</span>
        <div className="flex-1 border-t border-gray-200"></div>
      </div>
      <div className="mt-2" data-testid="plate-search-section">
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.plateLabel')}</label>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-4 flex min-w-0 h-[50px]">
            <EuroBand className="rounded-l-lg border-2 border-[#003399]" />
            <input
              type="text"
              data-testid="plate-input"
              placeholder="AA-123-BB"
              value={plate}
              onChange={(e) => { setPlate(formatPlate(e.target.value)); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              className="flex-1 min-w-0 border-2 border-l-0 border-gray-300 px-3 sm:px-4 rounded-r-lg focus:outline-none focus:border-orange-500 font-mono text-base sm:text-lg tracking-widest uppercase"
              maxLength={9}
            />
          </div>
          <Button data-testid="plate-search-button" onClick={search} disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 py-3 rounded-lg h-[50px]">
            {loading ? <Loader2 size={20} className="mr-2 animate-spin" /> : <Search size={20} className="mr-2" />}
            {t('home.searchBtn')}
          </Button>
        </div>
        {error && <p data-testid="plate-error" className="mt-2 text-sm text-red-600">{error}</p>}
        {result && !error && (
          <div data-testid="plate-result" className="mt-3 flex flex-wrap items-center justify-between gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              <Check size={16} className="inline mr-1" />
              {t('home.identified')} <strong>{result.brand} {result.model}</strong> ({result.year}) · {result.engine.name}
              {result.source === 'simulation' && <span className="ml-2 text-xs text-green-700/70">{t('home.simulated')}</span>}
            </p>
            <Button size="sm" data-testid="plate-see-parts-button" onClick={() => navigate('/pieces')} className="bg-green-600 hover:bg-green-700">{t('home.seeParts')}</Button>
          </div>
        )}
      </div>
    </>
  );
};

export const SelectedVehicleSummary = ({ brand, model, year, engine }) => {
  const { t } = useT();
  return (
    <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border border-orange-200">
      <div className="flex items-start gap-4">
        <VehicleBrandLogo brand={brand} model={model} size={72} preferModel zoom testId="selected-vehicle-logo" />
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 flex items-center gap-1.5"><Check size={16} className="text-orange-500" /> {t('home.selectedVehicle')}</h3>
          <p className="text-gray-700 mt-1">
            <span className="font-medium">{brand} {model}</span>
            <span className="text-gray-500"> ({year})</span>
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className={`text-xs px-2 py-1 rounded-full ${getFuelColor(engine.fuel)}`}>{getFuelLabel(engine.fuel)}</span>
            <span className="text-sm text-gray-600">{engine.name}</span>
            <span className="text-sm text-gray-500">•</span>
            <span className="text-sm text-gray-600">{engine.power}ch ({engine.power_kw}kW)</span>
            {engine.displacement > 0 && <><span className="text-sm text-gray-500">•</span><span className="text-sm text-gray-600">{engine.displacement} cm³</span></>}
            {engine.engine_code && <><span className="text-sm text-gray-500">•</span><span className="text-sm text-gray-500 font-mono">{engine.engine_code}</span></>}
          </div>
        </div>
      </div>
    </div>
  );
};
